//Libs
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
//Componentes material
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormLabel from '@material-ui/core/FormLabel';
import Grid from '@material-ui/core/Grid';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core';
//Componentes
import Imagem from '../Imagem';
//Utils
import { formatarNumero, validarDecimal } from '../../utils/validacao';
//Models
import AvaliacaoResposta from '../../models/AvaliacaoResposta';
import { TIPO_ESCOLHA } from '../../models/QuestionarioQuestao';

/** Importando documentação de modelos utilizados
 * @typedef {import('../../models/AvaliacaoOpcaoResposta').default} AvaliacaoOpcaoResposta
 */

/**
 * Estilos do componente
 */
const styles = () => ({
    fieldset: {
        width: '100%'
    }
});

/**
 * Componente para renderizar um campo para responder uma questão do tipo escolha
 */
class AvaliacaoRespostaEscolha extends Component {
    constructor(props) {
        super(props);
        //Bindings
        this.handleRespostaChange = this.handleRespostaChange.bind(this);
        this.handleOutroChange = this.handleOutroChange.bind(this);
        this.handleOpcaoOutroChange = this.handleOpcaoOutroChange.bind(this);
        //Estado inicial
        this.state = {
            outroSelecionado: !!props.questao.outro
        };
    }

    /**
     * Trata o evento de alteração da resposta
     * @param {*} event Dados do evento
     */
    handleRespostaChange(event) {
        //Copia os dados da questão
        const questao = AvaliacaoResposta.clone(this.props.questao);
        //Define a resposta de acordo com os dados recebidos e converte para número
        questao.resposta = event.target.value * 1;
        //Garante que a opção 'outro' não está preenchido
        questao.outro = null;
        //Dispara o evento de alteração
        if (this.props.onChange)
            this.props.onChange(questao);
        //Oculta o campo para preencher a opção 'outro'
        this.setState({ outroSelecionado: false });
    }

    /**
     * Trata quando a opção 'Outro' é marcada
     * @param {*} event Dados do evento
     */
    handleOpcaoOutroChange(event) {
        //Copia os dados da questão
        const questao = AvaliacaoResposta.clone(this.props.questao);
        //Caso esteja marcado, limpa qualquer resposta que esteja selecionada
        if (event.target.checked) {
            questao.resposta = null;  
        } else { //Caso esteja desmarcado, limpa a resposta 'outro'
            questao.outro = null;
        }
        //Dispara o evento de alteração
        if (this.props.onChange)
            this.props.onChange(questao);
        //Atualiza o estado do componente para exibir o campo de acordo com a seleção
        this.setState({ outroSelecionado: event.target.checked });
    }

    /**
     * Trata quando o campo com a resposta 'Outro' é alterada
     * @param {*} event Dados do evento
     */
    handleOutroChange(event) {
        const novoValor = event.target.value;
        if (this.validarValor(novoValor)) {
            //Copia os dados da questão
            const questao = AvaliacaoResposta.clone(this.props.questao);
            //Armazena a resposta digitada pelo usuário
            questao.outro = event.target.value;
            //Dispara o evento de alteração
            if (this.props.onChange)
                this.props.onChange(questao);
        }
    }

    /**
     * Valida a reposta, considerando o tipo e a configuração da questão
     * @param {String} valor Valor a ser validado
     * @returns {Boolean} Retorna verdadeiro caso o valor seja válido
     */
    validarValor(valor) {
        /** @type {AvaliacaoResposta} */
        const questao = this.props.questao;
        
        //Se o tipo da questão for `escolha` e deve exibir a resposta como gráfico
        if (questao.tipo === TIPO_ESCOLHA && questao.exibeGrafico) {
            return validarDecimal(valor);
        } else {
            //Caso contrário o valor é considerado válido
            return true;
        }
    }

    /**
     * Renderiza o campo de texto para a resposta livre, de acordo com a opção selecionada
     * @param {AvaliacaoResposta} questao Dados da questão
     * @param {*} erros Mensagens de erro da questão
     * @returns {Object} Retorna o componente ou `undefined`
     */
    renderCampoOutro(questao, erros) {
        if (this.state.outroSelecionado) {
            return (
                <TextField
                    type="text" 
                    placeholder="Descreva a resposta"
                    variant="standard" 
                    size="small"
                    fullWidth 
                    onChange={this.handleOutroChange}
                    value={questao.outro ?? ''} 
                    error={!!erros.outro}
                    helperText={erros.outro}
                />  
            );
        }
    }

    /**
     * Renderiza a opção 'Outro' de acordo com a configuração da questão
     * @param {AvaliacaoResposta} questao Dados da questão
     * @returns {Object} Retorna o componente ou `undefined`
     */
    renderOpcaoOutro(questao) {
        if (questao.opcaoOutros) {
            return (
                <FormControlLabel control={<Radio checked={this.state.outroSelecionado} onChange={this.handleOpcaoOutroChange} />} label="Outro" />
            );
        }
    }

    /**
     * Renderiza o label da opção de resposta de acordo com os dados
     * @param {AvaliacaoOpcaoResposta} opcao Dados da opção
     * @returns {Object} Retorna o componente ou a descrição da opção
     */
    renderLabelOpcao(opcao) {
        /** @type {AvaliacaoResposta} */
        const questao = this.props.questao;

        //Caso a questão gere um gráfico
        if (questao.exibeGrafico) {
            return formatarNumero(opcao.valor);
        //Caso a opção possua uma imagem
        } else if (opcao.imagem) {
            return (
                <Fragment>
                    <Imagem alt={opcao.descricao} src={opcao.imagem} />
                    <Typography>{opcao.descricao}</Typography>
                </Fragment>
            );
        } else {
            //Retorna apenas a descrição
            return opcao.descricao;
        }
    }

    render() {

        /** @type {AvaliacaoResposta} */
        const questao = this.props.questao;
        const erro = this.props.erro;
        
        //Identificando mensagem de erro (Erro geral tem preferência)
        let mensagemErro = '';
        if (erro.erroResposta)
            mensagemErro = erro.erroResposta;
        else 
            mensagemErro = erro.resposta;

        return (
            <Grid item xs={12} >
                <FormControl component="fieldset" className={this.props.classes.fieldset} error={!!mensagemErro} >
                    <FormLabel component="legend">{questao.titulo}</FormLabel>
                    <RadioGroup aria-label="escolha" name="radio-input" value={questao.resposta ?? ''} onChange={this.handleRespostaChange}>
                        <Grid container spacing={1} justify="space-between" >
                            {
                                questao.opcoes.map(opcao => (
                                    <Grid item key={opcao.id} xs={12} sm={6} md={3} >
                                        <FormControlLabel value={opcao.id} control={<Radio />} label={this.renderLabelOpcao(opcao)} />
                                    </Grid>
                                ))
                            }
                        </Grid>
                    </RadioGroup>
                    { this.renderOpcaoOutro(questao) }
                    { this.renderCampoOutro(questao, erro) }
                    <FormHelperText>{mensagemErro}</FormHelperText>
                </FormControl>
            </Grid>
        );
    }
}

AvaliacaoRespostaEscolha.propTypes = {
    /** Objeto contendo os dados da questão a ser respondida */
    questao: PropTypes.instanceOf(AvaliacaoResposta).isRequired,
    /** Evento disparado quando a resposta for alterada */
    onChange: PropTypes.func,
    /** Mensagens de erro da resposta */
    erro: PropTypes.shape({ 
        resposta: PropTypes.string,
        erroResposta: PropTypes.string
    }),
    /** Classes CSS para personalização do componente */
    classes: PropTypes.shape({
        fieldset: PropTypes.string
    })
};

AvaliacaoRespostaEscolha.defaultProps = {
    erro: {
        resposta: '',
        erroResposta: ''
    }
};

export default withStyles(styles)(AvaliacaoRespostaEscolha);